import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { ExplorePresentationProps } from './ExplorePropertiesContainer';
import { SearchFiltersTypes } from '../../../../reducers/search-filters/reducer';
import { resetFilters } from '../../../../helpers/search-filters';
import SortByContainer from '../../../search/components/search-filters/sort-by-input/SortByContainer';
import NawyLoadingIndicator from '../../../../new-components/NawyLoadingIndicator';
import AutoColumnsGrid from '../../../../new-components/display-system/AutoColumnsGrid';
import MapSearchResultsToCards from '../../../search/components/search-results/MapSearchResultsToCards';
import PaginationContainer from '../../../search/components/search-results/pagination/PaginationContainer';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useSearchContext } from '../../../search-new/context/SearchContext';
import ErrorComponent from '../../../../new-components/ui/ErrorComponent';
import ResetFilters from '../../components/explore-properties/ResetFilters';
import SaleTypeTabs from './SaleTypeTabs';
import FilterIcon from '../../../../new-components/svg-icons/FilterIcon';
import useExploreSearch from './hooks/CompoundResults';
import { useSorting } from '../../../search-new/hooks/Sorting.hooks';

const ExplorePropertiesPresentation = ({
  headline,
  entityDetails,
  changePageHandler,
  explorePropertiesRef,
  exploreLocation,
  saleTypes,
  handleTabChange,
  appliedFilters,
  pageNumber
}: ExplorePresentationProps) => {
  const { t } = useTranslation(['entity', 'common']);
  const router = useRouter();

  const { searchFiltersState, searchFiltersDispatch, setShowFilters } =
    useSearchContext();

  const searchResults = useExploreSearch(
    entityDetails?.id!,
    pageNumber,
    exploreLocation
  );
  const { sorting } = useSorting();

  const getInfo = () => {
    return {
      results: searchResults.loadedSearchResults,
      properties_count: searchResults.loadedSearchResultsCount,
      compounds_count: searchResults.loadedSearchResultsCount,
      isLoading: searchResults.isLoading,
      isError: searchResults.isError
    };
  };

  const getFilterBar = () => {
    return (
      <FilterBar>
        {entityDetails?.type === SearchFiltersTypes.COMPOUNDS ? (
          <SaleTypeTabs
            saleTypes={saleTypes}
            handleTabChange={handleTabChange}
          />
        ) : null}
      </FilterBar>
    );
  };

  const getCardsContainer = useCallback(() => {
    const { results, properties_count, compounds_count, isLoading, isError } =
      getInfo();

    if (isError) return <ErrorComponent />;

    if (isLoading) {
      return <NawyLoadingIndicator />;
    }

    if (
      (properties_count === 0 &&
        searchFiltersState.searchFilters.category == 'property') ||
      (compounds_count === 0 &&
        searchFiltersState.searchFilters.category == 'compound')
    ) {
      if (entityDetails?.type != SearchFiltersTypes.COMPOUNDS) {
        return <ResetFilters />;
      }
      const NoResultsIndicator = dynamic(
        () => import('../../../../new-components/NoResultsIndicator')
      );
      return (
        <NoResultsIndicator
          onClick={() => resetFilters(router, searchFiltersDispatch)}
          isResetButton={true}
        />
      );
    }

    return (
      <>
        <AutoColumnsGrid
          minWidth={'340px'}
          rowsGap={'16px'}
          columnsGap={'16px'}
        >
          <MapSearchResultsToCards
            searchResults={results as any}
            findType={
              entityDetails?.type === SearchFiltersTypes.COMPOUNDS
                ? 'property'
                : searchFiltersState.searchFilters.category
            }
            pageNumber={pageNumber}
            searchLocation={exploreLocation}
          />
        </AutoColumnsGrid>
        <PaginationContainer
          scrollPostion={explorePropertiesRef}
          appliedPageNumber={pageNumber}
          onChangePageHandler={pageNumber =>
            changePageHandler && changePageHandler(pageNumber)
          }
          totalResultsDisplayed={
            searchFiltersState.searchFilters.category == 'compound'
              ? compounds_count
              : properties_count
          }
        />
      </>
    );
  }, [searchResults, searchFiltersState.searchFilters.category, pageNumber]);

  const getExploreHeadlineText = () => {
    const { properties_count, compounds_count, isLoading } = getInfo();

    if (headline)
      return (
        <div className="title-section">
          <h2 className="headline-3 entity-title">
            {t(headline) + ' ' + (entityDetails ? entityDetails?.name : '')}
          </h2>
          {!isLoading ? (
            <span className="headline-subtitle">
              {properties_count || compounds_count} {t('results_available')}
            </span>
          ) : (
            <span />
          )}
        </div>
      );
    return (
      <div className="title-section">
        <span className="headline-3 entity-title">
          {t(
            searchFiltersState.searchFilters.category === 'property'
              ? 'properties'
              : 'compounds',
            { ns: 'common' }
          ) +
            ' ' +
            t('in', { ns: 'common' }) +
            ' ' +
            entityDetails?.name}
        </span>
        {!isLoading ? (
          <span className="headline-subtitle">
            {properties_count || compounds_count} {t('results_available')}
          </span>
        ) : (
          <span></span>
        )}
      </div>
    );
  };

  return (
    <ExploreContainer ref={explorePropertiesRef} className="explore-container">
      <div className="explore-header-bar">
        {getExploreHeadlineText()}
        <div className="search-actions">
          {exploreLocation !== 'DiscoverDeveloperPopup' && (
            <FilterBtn onClick={() => setShowFilters(true)}>
              <FilterIcon color="#fff" />
              {t('filter')}
              {appliedFilters && appliedFilters > 0 ? (
                <span className="filters-indicator">{appliedFilters}</span>
              ) : null}
            </FilterBtn>
          )}
          <SortByContainer
            sortByOptions={sorting}
            searchFiltersState={searchFiltersState}
            searchFiltersDispatch={searchFiltersDispatch}
            sortLocation={exploreLocation}
          />
        </div>
      </div>
      {getFilterBar()}

      <div className="cards-wrapper">{getCardsContainer()}</div>
    </ExploreContainer>
  );
};
const ExploreContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  gap: 24px;
  .cards-wrapper {
    position: relative;
    min-height: 450px;
  }

  .entity-title {
    margin-bottom: 0;
  }

  .explore-header-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    .title-section {
      display: flex;
      flex-direction: column;
      gap: 8px;

      span.headline-subtitle {
        font-size: 14px;
        color: #656565;
      }
    }
    .search-actions {
      display: flex;
      align-items: center;
      width: auto;
      gap: 8px;
      button {
        font-family: materialMedium;
        border: 1px solid #e4ebf2;
        border-radius: 8px;
      }

      .menu {
        inset-inline-start: unset;
        inset-inline-end: 0px;
      }
    }

    .filters {
      max-width: calc(100% - 18px);
      height: 100%;
      display: flex;
      align-items: center;
    }
  }
`;

const FilterBar = styled.div`
  position: sticky;
  top: 0px;
  width: 100%;
  background-color: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
`;

const FilterBtn = styled.button`
  background-color: #015c9a;
  outline: none;
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  color: #fff;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-family: materialMedium;
  line-height: 150%;
  cursor: pointer;
  svg {
    width: 20px;
    height: 20px;
  }

  span {
    font-size: 10px;
    color: ${props => props.theme.light.colors.white};
    background-color: ${props => props.theme.light.colors.primaryOrange};
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
`;

export default ExplorePropertiesPresentation;
